// contact-form.ts
//
// Wzorcowy, reużywalny komponent formularza kontaktowego (Lit + valibot).
// Zastępuje funkcjonalnie React ContactForm.tsx przy ułamku wagi bundla
// (~5–10 KB gzip zamiast ~72 KB) i jest spójny z resztą stosu (Lit),
// którego używasz już m.in. w SiteHealth.ts, menu9.
//
// UŻYCIE (EJS) - teksty w języku strony z LD (welcome.<język>.json, contactForm):
//   <contact-form texts="<%= JSON.stringify(ld.contactForm) %>"></contact-form>
//   <contact-form endpoint="/api/contact" texts="..."></contact-form>
//
// PONOWNE UŻYCIE NA INNYCH PROJEKTACH:
//   Skopiuj plik, dopasuj FIELDS i schemat (jeśli inny zestaw pól)
//   i podaj inny `endpoint` przez atrybut HTML — reszta logiki zostaje bez zmian.

import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import * as v from 'valibot';

type FieldName = 'name' | 'email' | 'subject' | 'message';

/** Teksty formularza z LD strony (contactForm) - komponent nie zna języka. */
export interface ContactFormTexts {
    heading: string;
    fields: Record<FieldName, { label: string; placeholder: string }>;
    errors: Record<FieldName, string>;
    submit: string;
    sending: string;
    success: string;
    sendError: string;
    rateLimited: string;
    networkError: string;
}

// -----------------------------------------------------------------------
// SCHEMAT WALIDACJI - komunikaty z LD, reguły takie same w każdym języku.
// -----------------------------------------------------------------------
function contactSchema(errors: ContactFormTexts['errors']) {
    return v.object({
        name: v.pipe(v.string(), v.trim(), v.minLength(2, errors.name)),
        email: v.pipe(v.string(), v.trim(), v.email(errors.email)),
        subject: v.pipe(v.string(), v.trim(), v.minLength(3, errors.subject)),
        message: v.pipe(v.string(), v.trim(), v.minLength(10, errors.message)),
    });
}

interface FieldConfig {
    name: FieldName;
    kind: 'input' | 'textarea';
    type?: string;
}

interface ContactApiResponse {
    success: boolean;
}

// -----------------------------------------------------------------------
// KONFIGURACJA PÓL — punkt, który zmieniasz przy adaptacji wzorca
// na inny formularz (inne pola = inna lista poniżej + inny schemat).
// -----------------------------------------------------------------------
const FIELDS: readonly FieldConfig[] = [
    { name: 'name', kind: 'input', type: 'text' },
    { name: 'email', kind: 'input', type: 'email' },
    { name: 'subject', kind: 'input', type: 'text' },
    { name: 'message', kind: 'textarea' },
] as const;

@customElement('contact-form')
export class ContactForm extends LitElement {
    // Renderowanie w Light DOM – stylowanie przejmuje globalny arkusz SCSS,
    // Style: components/_contact-form.scss
    protected createRenderRoot(): HTMLElement {
        return this;
    }

    /* =======================
     * WŁAŚCIWOŚCI KONFIGUROWALNE PRZEZ ATRYBUTY HTML
     * (to jest to, co czyni komponent "wzorcem" — inne projekty
     *  ustawiają tylko atrybuty, bez kopiowania logiki)
     * ======================= */

    @property({ type: String })
    endpoint = '/api/contact';

    /** Wymagane: teksty w języku strony (atrybut `texts` z JSON-em LD). */
    @property({ type: Object })
    texts!: ContactFormTexts;

    private get schema() {
        return contactSchema(this.texts.errors);
    }

    /* =======================
     * STAN WEWNĘTRZNY
     * ======================= */

    @state()
    private errors: Partial<Record<FieldName, string>> = {};

    @state()
    private status = '';

    @state()
    private statusOk = false;

    @state()
    private submitting = false;

    /* =======================
     * WALIDACJA
     * ======================= */

    private readFormData(form: HTMLFormElement): Record<string, string> {
        return Object.fromEntries(new FormData(form)) as Record<string, string>;
    }

    // Walidacja pojedynczego pola przy opuszczeniu (onBlur) —
    // odpowiednik mode: 'onBlur' z react-hook-form w oryginale.
    private validateFieldOnBlur(name: FieldName, form: HTMLFormElement) {
        const data = this.readFormData(form);
        const result = v.safeParse(this.schema, data);

        if (result.success) {
            if (this.errors[name]) {
                const next = { ...this.errors };
                delete next[name];
                this.errors = next;
            }
            return;
        }

        const issue = result.issues.find((i) => i.path?.[0]?.key === name);
        if (issue) {
            this.errors = { ...this.errors, [name]: issue.message };
        }
    }

    // Ukrywa błąd natychmiast po rozpoczęciu poprawiania pola —
    // odpowiednik onChange: () => clearErrors(name) w oryginale.
    private clearFieldError(name: FieldName) {
        if (this.errors[name]) {
            const next = { ...this.errors };
            delete next[name];
            this.errors = next;
        }
    }

    /* =======================
     * SUBMIT
     * ======================= */

    private async onSubmit(e: SubmitEvent) {
        e.preventDefault();

        const form = e.currentTarget as HTMLFormElement;
        const data = this.readFormData(form);
        const result = v.safeParse(this.schema, data);

        if (!result.success) {
            const nextErrors: Partial<Record<FieldName, string>> = {};
            for (const issue of result.issues) {
                const key = issue.path?.[0]?.key as FieldName | undefined;
                if (key && !nextErrors[key]) nextErrors[key] = issue.message;
            }
            this.errors = nextErrors;
            return;
        }

        this.errors = {};
        this.submitting = true;
        this.status = '';

        try {
            const response = await fetch(this.endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(result.output),
            });

            const json = (await response.json().catch(() => ({}))) as Partial<ContactApiResponse>;

            if (response.ok && json.success) {
                this.statusOk = true;
                this.status = this.texts.success;
                form.reset();
            } else {
                // Treść odpowiedzi serwera nie jest w języku strony - komunikat z LD według statusu.
                this.statusOk = false;
                this.status = response.status === 429 ? this.texts.rateLimited : this.texts.sendError;
            }
        } catch {
            this.statusOk = false;
            this.status = this.texts.networkError;
        } finally {
            this.submitting = false;
        }
    }

    /* =======================
     * RENDER
     * ======================= */

    private renderField(field: FieldConfig) {
        const error = this.errors[field.name];

        const onBlur = (e: Event) => {
            const form = (e.currentTarget as HTMLElement).closest('form');
            if (form) this.validateFieldOnBlur(field.name, form);
        };

        const onInput = () => this.clearFieldError(field.name);

        return html`
            <div class="form-group">
                <label for="${field.name}">
                    ${this.texts.fields[field.name].label}
                    <span class="required" aria-hidden="true">*</span>
                </label>

                ${
                    field.kind === 'textarea'
                        ? html`
                              <textarea
                                  id="${field.name}"
                                  name="${field.name}"
                                  placeholder="${this.texts.fields[field.name].placeholder}"
                                  @blur="${onBlur}"
                                  @input="${onInput}"
                              ></textarea>
                          `
                        : html`
                              <input
                                  id="${field.name}"
                                  name="${field.name}"
                                  type="${field.type ?? 'text'}"
                                  placeholder="${this.texts.fields[field.name].placeholder}"
                                  @blur="${onBlur}"
                                  @input="${onInput}"
                              />
                          `
                }
                ${
                    error
                        ? html`
                              <div class="error-message">${error}</div>
                          `
                        : ''
                }
            </div>
        `;
    }

    render() {
        return html`
            <div id="arti24Kontakt">
                <div class="contact-form">
                    <h2>${this.texts.heading}</h2>

                    <form @submit="${this.onSubmit}" novalidate>
                        ${FIELDS.map((field) => this.renderField(field))}

                        <button type="submit" ?disabled="${this.submitting}">
                            ${this.submitting ? this.texts.sending : this.texts.submit}
                        </button>
                    </form>

                    ${
                        this.status
                            ? html`
                                  <p
                                      class="status-message ${this.statusOk ? 'status-message--ok' : 'status-message--error'}"
                                  >
                                      ${this.status}
                                  </p>
                              `
                            : ''
                    }
                </div>
            </div>
        `;
    }

    // Style celowo NIE są tu zdefiniowane — komponent renderuje się w Light DOM
    // (patrz createRenderRoot powyżej), więc stylowanie przejmuje zewnętrzny
    // arkusz: components/_contact-form.scss.
}

declare global {
    interface HTMLElementTagNameMap {
        'contact-form': ContactForm;
    }
}
